import { getUserProfileRepresentatives } from '@/api/backend/profile';
import { ProfileRepresentativeResponseType } from '@/api/backend/types';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { formatIsoDate } from '@/lib/utils';
import {
  Badge,
  Button,
  Card,
  DataTableColumnType,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileContext } from '../../route';
import { CompanyRepresentativeForm } from './CompanyRepresentativeForm';
import { AddFilled } from '@/assets/icons';

export const CompanyRepresentatives = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { profile } = useContext(ProfileContext);
  const [isCreateRepresentativeOpen, setIsCreateRepresentativeOpen] = useState(false);

  const { data: representativesData, isLoading: isRepresentativesLoading } = useQuery({
    queryKey: ['profileRepresentatives', profile.id],
    queryFn: getUserProfileRepresentatives,
  });

  if (isCreateRepresentativeOpen) {
    return <CompanyRepresentativeForm onClose={() => setIsCreateRepresentativeOpen(false)} />;
  }

  const columns: DataTableColumnType<ProfileRepresentativeResponseType>[] = [
    {
      title: t('profile.company.tabs.representatives.table.header.user'),
      render: (item) => (
        <Link
          className="text-left"
          variant="bold"
          onClick={() => {
            navigate({
              to: `/profile/company/representative/$representativeId`,
              params: { representativeId: item.id.toString() },
            });
          }}
        >
          {item.fullName}
        </Link>
      ),
    },
    {
      title: t('profile.company.tabs.representatives.table.header.status'),
      render: (item) => (
        <div className="flex flex-col gap-2">
          <div>
            {item.accountStatus === 'ACTIVE' ? (
              <Badge type="positive" filled>
                {t('profile.company.tabs.representatives.table.row.active')}
              </Badge>
            ) : (
              <Badge type="negative" filled>
                {t('profile.company.tabs.representatives.table.row.revoked')}
              </Badge>
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            {t('profile.company.tabs.representatives.table.row.lastLogin', {
              date: formatIsoDate(item.lastLogin, i18n.language),
            })}
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center flex-wrap">
        <h1 className="text-foreground text-3xl font-bold mb-4">
          {t('profile.company.tabs.representatives.section.title')}
        </h1>

        <Button onClick={() => setIsCreateRepresentativeOpen(true)}>
          <AddFilled /> {t('profile.company.tabs.representatives.actions.add')}
        </Button>
      </div>

      <p className="text-primary mb-4">{t('profile.company.tabs.representatives.section.description')}</p>

      {isRepresentativesLoading ? (
        <Skeleton className="h-[200px] w-full" />
      ) : (
        <Card className="w-full p-5 shadow-lg border-none">
          {(representativesData ?? []).length > 0 ? (
            <ResponsiveDataView
              columns={columns}
              data={representativesData ?? []}
              emptyMessage={t('profile.company.tabs.representatives.table.notFound')}
              getRowKey={(item) => item.id}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('profile.company.tabs.representatives.table.header.user')}</TableHead>
                    <TableHead className="text-end">
                      {t('profile.company.tabs.representatives.table.header.status')}
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {representativesData?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-[#113561] font-bold text-base underline">
                        <Link
                          className="text-left"
                          variant="bold"
                          onClick={() => {
                            navigate({
                              to: `/profile/company/representative/$representativeId`,
                              params: { representativeId: item.id.toString() },
                            });
                          }}
                        >
                          {item.fullName}
                        </Link>
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-col items-end justify-end gap-2">
                          <div>
                            {item.accountStatus === 'ACTIVE' ? (
                              <Badge type="positive" filled>
                                {t('profile.company.tabs.representatives.table.row.active')}
                              </Badge>
                            ) : (
                              <Badge type="negative" filled>
                                {t('profile.company.tabs.representatives.table.row.revoked')}
                              </Badge>
                            )}
                          </div>

                          <span className="text-sm text-muted-foreground">
                            {t('profile.company.tabs.representatives.table.row.lastLogin', {
                              date: formatIsoDate(item.lastLogin, i18n.language),
                            })}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveDataView>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-[#52525B]">{t('profile.company.tabs.representatives.table.notFound')}</p>
            </div>
          )}
        </Card>
      )}
    </>
  );
};
